import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './components/LoginPage';
import ActivitySelector from './components/ActivitySelector';
import ActivitySelectionList from './components/ActivitySelectionList';
import ActivityLegend from './components/ActivityLegend';
import MapViewer from './components/MapViewer';
import Scrubber from './components/Scrubber';
import DevicesPage from './components/DevicesPage';
import DevPage from './components/DevPage';
import ERDPage from './components/erd/ERDPage';
import CollectionEditor from './components/CollectionEditor';
import { fetchGates, fetchGateCrossings, fetchActivities, fetchActivitySummaries, fetchDevices, fetchGateAssignments } from './services/api';
import { Search, Map, Smartphone, FlaskConical, Sun, Moon, Database, FileEdit } from './components/Icons';
import { useBookmarks } from './hooks/useBookmarks';

const ACTIVITY_COLORS = [
  '#FF6B35', '#F7931E', '#FDC830', '#4CAF50', '#00ACC1',
  '#5E35B1', '#D81B60', '#6D4C41', '#00897B', '#E53935'
];

function AppContent() {
  const { user, logout, authRequired } = useAuth();
  const [gates, setGates] = useState([]);
  const [crossings, setCrossings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [gateAssignments, setGateAssignments] = useState({});
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [navCollapsed, setNavCollapsed] = useState(false);

  const [theme, setTheme] = useState(() => localStorage.getItem('hightag-theme') || 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('hightag-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0d1117' : '#ffffff');
  }, [theme]);
  const toggleTheme = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);

  // Query & Selection
  const [availableActivities, setAvailableActivities] = useState([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // Plotting
  const [plottedActivitiesData, setPlottedActivitiesData] = useState([]);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotError, setPlotError] = useState(null);

  // Time playback
  const [timeHead, setTimeHead] = useState(1000);
  const [timeTail, setTimeTail] = useState(0);
  const [showPointMarkers, setShowPointMarkers] = useState(true);
  const [visibleActivities, setVisibleActivities] = useState(new Set());
  const [accuracyBounds, setAccuracyBounds] = useState({ min: 2, max: 50 });
  const [activityColors, setActivityColors] = useState({});

  const { bookmarks, flagActivity, setActivityNote, flagRider, setRiderAlias, setRiderNote } = useBookmarks();

  // KMZ / KML overlay state
  const [kmzOverlays, setKmzOverlays] = useState([]);
  const handleAddKmzOverlay = useCallback((parsed) => {
    const overlayId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setKmzOverlays(prev => [...prev, {
      id: overlayId,
      filename: parsed.filename,
      featureCount: parsed.featureCount,
      visible: true,
      opacity: 0.7,
      groups: parsed.groups.map((g, i) => ({
        id: `${overlayId}-g${i}`,
        name: g.name,
        path: g.path,
        features: g.features,
        featureCount: g.featureCount,
        color: g.color,
        visible: true,
      })),
    }]);
  }, []);
  const handleRemoveKmzOverlay = useCallback((id) => {
    setKmzOverlays(prev => prev.filter(o => o.id !== id));
  }, []);
  const handleToggleKmzOverlay = useCallback((id) => {
    setKmzOverlays(prev => prev.map(o => o.id === id ? { ...o, visible: !o.visible } : o));
  }, []);
  const handleToggleKmzGroup = useCallback((overlayId, groupId) => {
    setKmzOverlays(prev => prev.map(o =>
      o.id === overlayId
        ? { ...o, groups: o.groups.map(g => g.id === groupId ? { ...g, visible: !g.visible } : g) }
        : o
    ));
  }, []);
  const handleKmzOpacityChange = useCallback((id, opacity) => {
    setKmzOverlays(prev => prev.map(o => o.id === id ? { ...o, opacity } : o));
  }, []);

  useEffect(() => {
    Promise.all([fetchGates(), fetchGateCrossings(), fetchDevices(), fetchGateAssignments()])
      .then(([g, c, d, ga]) => {
        setGates(g);
        setCrossings(c);
        setDevices(d);
        setGateAssignments(ga);
      })
      .catch(err => console.error('Error loading static data:', err));
  }, []);

  const handleGatesUpdated = useCallback(() => {
    fetchGates()
      .then(g => setGates(g))
      .catch(err => console.error('Error refreshing gates:', err));
  }, []);

  const handleDeviceSelect = useCallback((deviceId) => {
    setSelectedDeviceId(deviceId);
    setActiveTab(3); // Switch to devices tab
  }, []);

  const handleQueryActivities = async (queryParams) => {
    setQueryLoading(true);
    setQueryError(null);
    try {
      const [summaries, crossingsData] = await Promise.all([
        fetchActivitySummaries(queryParams),
        fetchGateCrossings()
      ]);
      setCrossings(crossingsData);
      setAvailableActivities(summaries);
      setSelectedActivityIds(summaries.map(s => s.activityId));
    } catch (err) {
      setQueryError('Failed to fetch activities: ' + err.message);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleClearResults = () => { setAvailableActivities([]); setSelectedActivityIds([]); setQueryError(null); };
  const handleSelectionChange = (ids) => setSelectedActivityIds(ids);

  const handlePlotSelected = async () => {
    if (selectedActivityIds.length === 0) return;
    setPlotLoading(true);
    setPlotError(null);
    try {
      const fullData = await fetchActivities({ by: 'id_list', activityIds: JSON.stringify(selectedActivityIds) });
      setPlottedActivitiesData(fullData);
      setVisibleActivities(new Set(selectedActivityIds));
      const colors = {};
      selectedActivityIds.forEach((id, i) => { colors[id] = ACTIVITY_COLORS[i % ACTIVITY_COLORS.length]; });
      setActivityColors(colors);
      setTimeHead(1000);
      setTimeTail(0);
      setShowPointMarkers(true);
      setActiveTab(1);
    } catch (err) {
      setPlotError('Failed to load activity data: ' + err.message);
    } finally {
      setPlotLoading(false);
    }
  };

  const handleToggleActivity = useCallback((activityId, forceState = null) => {
    setVisibleActivities(prev => {
      const s = new Set(prev);
      if (forceState === true) s.add(activityId);
      else if (forceState === false) s.delete(activityId);
      else s.has(activityId) ? s.delete(activityId) : s.add(activityId);
      return s;
    });
  }, []);

  const handleAccuracyBoundsChange = useCallback((min, max) => setAccuracyBounds({ min, max }), []);
  const handleColorChange = useCallback((id, color) => setActivityColors(prev => ({ ...prev, [id]: color })), []);

  const plottedActivityIds = useMemo(() => [...new Set(plottedActivitiesData.map(i => i.activityId))], [plottedActivitiesData]);

  const globalTimeRange = useMemo(() => {
    if (plottedActivitiesData.length === 0) return { min: 0, max: 0 };
    let mn = Infinity, mx = -Infinity;
    for (const p of plottedActivitiesData) {
      if (p.timestamp) { const t = new Date(p.timestamp).getTime(); if (t < mn) mn = t; if (t > mx) mx = t; }
    }
    return mn === Infinity ? { min: 0, max: 0 } : { min: mn, max: mx };
  }, [plottedActivitiesData]);

  const timeWindow = useMemo(() => {
    const { min, max } = globalTimeRange;
    if (max <= min) return { head: null, tail: null };
    return {
      head: timeHead >= 1000 ? null : min + (timeHead / 1000) * (max - min),
      tail: timeTail <= 0 ? null : min + (timeTail / 1000) * (max - min),
    };
  }, [timeHead, timeTail, globalTimeRange]);

  const hasPlotData = plottedActivityIds.length > 0;

  const NAV_ITEMS = [
    { section: 'GPS Analysis' },
    { key: 0, label: 'Query', icon: <Search size={18} /> },
    { key: 1, label: 'Map', icon: <Map size={18} />, disabled: !hasPlotData, badge: plottedActivityIds.length || null },
    { key: 2, label: 'Dev Sandbox', icon: <FlaskConical size={18} />, disabled: !hasPlotData },
    { section: 'Fleet' },
    { key: 3, label: 'Devices', icon: <Smartphone size={18} /> },
    { section: 'Tools' },
    { key: 4, label: 'Schema ERD', icon: <Database size={18} /> },
    { key: 5, label: 'Collection Editor', icon: <FileEdit size={18} /> },
  ];

  return (
    <div className="app-shell">
      {/* ──── Left Navigation ──── */}
      <nav className={`app-nav ${navCollapsed ? 'collapsed' : ''}`}>
        <div className="nav-brand" onClick={() => setNavCollapsed(c => !c)}>
          <div className="nav-logo">
            <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
              <path d="M3 20 L12 4 L22 4 L22 10 L14.5 10 L8.5 20Z" />
            </svg>
          </div>
          {!navCollapsed && <span className="nav-wordmark">Hightag</span>}
        </div>

        <div className="nav-items">
          {NAV_ITEMS.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section">{!navCollapsed && item.section}</div>
            ) : (
              <button key={item.key}
                className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
                onClick={() => !item.disabled && setActiveTab(item.key)}
                disabled={item.disabled}
                title={navCollapsed ? item.label : undefined}
              >
                <span className="nav-item-icon">{item.icon}</span>
                {!navCollapsed && <span className="nav-item-label">{item.label}</span>}
                {!navCollapsed && item.badge && <span className="nav-item-badge">{item.badge}</span>}
              </button>
            )
          )}
        </div>

        <div className="nav-footer">
          <button className="theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            {!navCollapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
          {authRequired && user && !navCollapsed && (
            <div className="nav-user">
              <div className="nav-avatar">{(user.displayName || user.email || 'U')[0].toUpperCase()}</div>
              <div className="nav-user-info">
                <span className="nav-user-name">{user.displayName || user.email}</span>
                <button className="nav-signout" onClick={logout}>Sign Out</button>
              </div>
            </div>
          )}
          {authRequired && user && navCollapsed && (
            <div className="nav-user-collapsed" title={user.displayName || user.email}>
              <div className="nav-avatar">{(user.displayName || user.email || 'U')[0].toUpperCase()}</div>
            </div>
          )}
        </div>
      </nav>

      {/* ──── Main Content ──── */}
      <main className="app-main">

        {/* Query Page */}
        <div className="page" style={{ display: activeTab === 0 ? 'flex' : 'none' }}>
          <aside className="page-sidebar">
            <div className="page-sidebar-header">
              <h2>GPS Activities</h2>
            </div>
            <div className="page-sidebar-body">
              <ActivitySelector
                onQuery={handleQueryActivities}
                loading={queryLoading}
                onClearResults={handleClearResults}
                bookmarks={bookmarks}
              />
              {queryError && <div className="msg msg-error">{queryError}</div>}
              {plotError && <div className="msg msg-error">{plotError}</div>}
              {availableActivities.length > 0 && (
                <div className="query-stats">
                  <span>Available: <strong>{availableActivities.length}</strong></span>
                  <span>Selected: <strong>{selectedActivityIds.length}</strong></span>
                  {plottedActivityIds.length > 0 && (
                    <span>Plotted: <strong>{plottedActivityIds.length}</strong></span>
                  )}
                </div>
              )}
            </div>
          </aside>
          <div className="page-content">
            <ActivitySelectionList
              activities={availableActivities}
              selectedIds={selectedActivityIds}
              onSelectionChange={handleSelectionChange}
              onPlotSelected={handlePlotSelected}
              bookmarks={bookmarks}
              onFlagActivity={flagActivity}
              onSetActivityNote={setActivityNote}
              onFlagRider={flagRider}
              onSetRiderAlias={setRiderAlias}
              onSetRiderNote={setRiderNote}
            />
            {plotLoading && (
              <div className="loading-overlay">
                <div className="loading-card">Loading GPS data...</div>
              </div>
            )}
          </div>
        </div>

        {/* Map Page */}
        <div className="page" style={{ display: activeTab === 1 ? 'flex' : 'none' }}>
          {hasPlotData ? (
            <>
              <aside className="page-sidebar">
                <div className="page-sidebar-header">
                  <h2>Legend</h2>
                  <span className="header-meta">{plottedActivityIds.length} activities &middot; {plottedActivitiesData.length} pts</span>
                </div>
                <div className="page-sidebar-body">
                  <ActivityLegend
                    activities={plottedActivitiesData}
                    activityIds={plottedActivityIds}
                    activityColors={activityColors}
                    visibleActivities={visibleActivities}
                    onToggleActivity={handleToggleActivity}
                    onColorChange={handleColorChange}
                    kmzOverlays={kmzOverlays}
                    onAddKmzOverlay={handleAddKmzOverlay}
                    onRemoveKmzOverlay={handleRemoveKmzOverlay}
                    onToggleKmzOverlay={handleToggleKmzOverlay}
                    onToggleKmzGroup={handleToggleKmzGroup}
                    onKmzOpacityChange={handleKmzOpacityChange}
                  />
                </div>
              </aside>
              <div className="page-content map-content">
                <div className="map-container">
                  <MapViewer
                    gates={gates}
                    crossings={crossings}
                    activities={plottedActivitiesData}
                    selectedActivityIds={plottedActivityIds}
                    activityColors={activityColors}
                    visibleActivities={visibleActivities}
                    onToggleActivity={handleToggleActivity}
                    timeWindow={timeWindow}
                    showPointMarkers={showPointMarkers}
                    accuracyBounds={accuracyBounds}
                    onAccuracyBoundsChange={handleAccuracyBoundsChange}
                    kmzOverlays={kmzOverlays}
                    onGatesUpdated={handleGatesUpdated}
                    devices={devices}
                    gateAssignments={gateAssignments}
                    onDeviceSelect={handleDeviceSelect}
                  />
                  <Scrubber
                    head={timeHead} tail={timeTail}
                    onHeadChange={setTimeHead} onTailChange={setTimeTail}
                    globalTimeRange={globalTimeRange}
                    showPointMarkers={showPointMarkers}
                    onTogglePointMarkers={() => setShowPointMarkers(p => !p)}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <Search size={40} />
              <h3>No activities plotted</h3>
              <p>Go to Query to select activities to visualize.</p>
              <button className="btn btn-accent" onClick={() => setActiveTab(0)}>Open Query</button>
            </div>
          )}
        </div>

        {/* Dev Page */}
        <div className="page page-full" style={{ display: activeTab === 2 ? 'flex' : 'none' }}>
          {hasPlotData ? (
            <DevPage activities={plottedActivitiesData} activityIds={plottedActivityIds} activityColors={activityColors} theme={theme} kmzOverlays={kmzOverlays} />
          ) : (
            <div className="empty-state">
              <FlaskConical size={40} />
              <h3>No data loaded</h3>
              <p>Query and plot activities first to use the Dev Sandbox.</p>
              <button className="btn btn-accent" onClick={() => setActiveTab(0)}>Open Query</button>
            </div>
          )}
        </div>

        {/* Devices Page */}
        <div className="page page-full" style={{ display: activeTab === 3 ? 'flex' : 'none' }}>
          <DevicesPage initialSelectedDevice={selectedDeviceId} />
        </div>

        {/* ERD Page */}
        <div className="page page-full" style={{ display: activeTab === 4 ? 'flex' : 'none' }}>
          <ERDPage />
        </div>

        {/* Collection Editor Page */}
        <div className="page page-full" style={{ display: activeTab === 5 ? 'flex' : 'none' }}>
          <CollectionEditor />
        </div>

      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { loading, authRequired, isAuthenticated, accessDenied } = useAuth();
  if (loading || authRequired === null) {
    return (
      <div className="auth-loading">
        <div className="auth-spinner" />
        <span>Loading...</span>
      </div>
    );
  }
  if (!authRequired) return <AppContent />;
  if (!isAuthenticated || accessDenied) return <LoginPage />;
  return <AppContent />;
}

export default App;
